import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {setWindowTitle} from 'BenchlingUtil';
import ChemicalCounterpartLibrary from 'chemistry/ChemicalCounterpartLibrary';
import {archiveSolvates, createSolvate, unarchiveSolvates, updateSolvate} from 'chemistry/ChemistryDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ErrorFragment} from 'graphql-client/Error';
import {TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';
import {withLoadingScreen} from 'util/Promises';

import {SolvateLibraryQuery} from './__generated/SolvateLibraryQuery';

export const SOLVATE_LIBRARY_QUERY = gql`
  query SolvateLibraryQuery @skipAudit @engComponent(name: CHEMISTRY) {
    solvates {
      id
      name
      code
      calculatedMolecularWeight
      canonicalSmiles
      archiveData {
        archived
        purpose
      }
      imageData {
        ... on SVGNode {
          base64Svg
        }
        ...Error
      }
    }
  }
  ${ErrorFragment.fragment}
`;

export default function SolvateLibrary(): JSX.Element {
  useEffect(() => {
    setWindowTitle('Solvates');
  }, []);

  const result = useBenchlingQuery<SolvateLibraryQuery>(SOLVATE_LIBRARY_QUERY);
  return (
    <ChemicalCounterpartLibrary
      counterpartName="solvate"
      items={result.type === QueryResultType.SUCCESS ? result.data.solvates : []}
      loading={result.type === QueryResultType.PENDING}
      tableKey={TABLE_KEYS.SOLVATE_LIBRARY_TABLE}
      onAddNew={async (name: string, code: string, sketcherValue: string) => {
        await createSolvate(name, sketcherValue, code);
        // Refetch GQL query to include new solvate in the results.
        await result.refetch();
      }}
      onEdit={async (id: string, name: string, code: string) => {
        await updateSolvate(id, name, code);
        await result.refetch();
      }}
      onArchive={async (solvateIds, purpose) => {
        await withLoadingScreen(archiveSolvates(solvateIds, purpose));
        await result.refetch();
      }}
      onUnarchive={async (solvateIds) => {
        await withLoadingScreen(unarchiveSolvates(solvateIds));
        await result.refetch();
      }}
    />
  );
}
